import { useState, useRef, useEffect } from 'react';
import { isNativePlatform } from '../lib/capacitor';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Browser } from '@capacitor/browser';
import './SOSButton.css';

const HOLD_DURATION = 3000; // 3 seconds

export default function SOSButton() {
  const [isHolding, setIsHolding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [progress, setProgress] = useState(0);
  const holdTimer = useRef(null);
  const progressInterval = useRef(null);
  const startTime = useRef(null);

  // Handle press start
  const handlePressStart = (e) => {
    e.preventDefault();
    if (showConfirm) return;
    setIsHolding(true);
    startTime.current = Date.now();

    // Start progress animation
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      const newProgress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setProgress(newProgress);
    }, 50);

    // Set timer to trigger SOS
    holdTimer.current = setTimeout(() => {
      triggerSOS();
    }, HOLD_DURATION);
  };

  // Handle press end
  const handlePressEnd = () => {
    setIsHolding(false);
    setProgress(0);
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
    }
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
  };

  // Trigger SOS - show confirmation first
  const triggerSOS = () => {
    handlePressEnd();

    // Haptic feedback
    if (isNativePlatform()) {
      Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {});
    } else if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }

    setShowConfirm(true);
  };

  // Confirm and call 911
  const confirmCall = async () => {
    setShowConfirm(false);

    // Use multiple fallback approaches to ensure the dialer opens
    const telUrl = 'tel:911';

    if (isNativePlatform()) {
      // On native, use Capacitor Browser to handle tel: scheme reliably
      try {
        await Browser.open({ url: telUrl });
      } catch {
        // Fallback to window.open which the OS may intercept
        window.open(telUrl, '_system');
      }
    } else {
      // On web, window.location.href works for tel: links
      window.location.href = telUrl;
    }
  };

  // Cancel SOS
  const cancelCall = () => {
    setShowConfirm(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, []);

  return (
    <>
      <button
        className={`sos-button ${isHolding ? 'holding' : ''}`}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressEnd}
        aria-label="Emergency SOS - Hold for 3 seconds to call 911"
      >
        <div
          className="sos-progress"
          style={{
            background: `conic-gradient(#fff ${progress}%, transparent ${progress}%)`
          }}
        />
        <div className="sos-inner">
          <span className="sos-text">SOS</span>
        </div>
        {isHolding && (
          <div className="sos-countdown">
            {Math.ceil((HOLD_DURATION - progress * HOLD_DURATION / 100) / 1000)}
          </div>
        )}
      </button>

      {showConfirm && (
        <div className="sos-confirm-overlay">
          <div className="sos-confirm-dialog" role="alertdialog" aria-modal="true">
            <div className="sos-confirm-icon">911</div>
            <h3 className="sos-confirm-title">Call 911?</h3>
            <p className="sos-confirm-text">This will open your phone dialer to call emergency services.</p>
            <div className="sos-confirm-actions">
              <button className="sos-confirm-cancel" onClick={cancelCall}>Cancel</button>
              <button className="sos-confirm-call" onClick={confirmCall}>Call 911</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
